import * as React from 'react';
import { addDays, format } from 'date-fns';
import { Button } from '~/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '~/components/shared/states';
import { useAddresses, useAvailability } from '~/lib/queries';
import { cn, formatDuration, formatRelativeDay, formatTime } from '~/lib/utils';
import type { BookingDraft } from '../booking-types';
import { StepShell } from '../step-shell';

/**
 * Tunnel de reservation — parcours principal de l'application.
 *
 * L'ordre des etapes suit litteralement l'enonce : adresse, cycle, operation de
 * maintenance, forfait, creneau, complements. Deux partis pris :
 *
 *   - La couverture est verifiee DES l'etape 1. Un client hors zone l'apprend
 *     immediatement, avant d'avoir saisi quoi que ce soit d'autre — plutot qu'apres
 *     cinq etapes, ce qui serait une perte de temps et une mauvaise experience.
 *
 *   - Le compte n'est jamais un prealable (RG-04). Un visiteur connecte se voit
 *     simplement proposer ses adresses et ses cycles enregistres.
 */

export function SlotStep({
  draft,
  coords,
  patch,
  onBack,
  onNext,
}: {
  draft: BookingDraft;
  coords: { lat: number; lng: number } | null;
  patch: (values: Partial<BookingDraft>) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const { data: addresses } = useAddresses(!!draft.addressId);

  // Une adresse enregistree ne porte pas ses coordonnees dans le brouillon :
  // on les recupere depuis la liste du compte.
  const resolved = React.useMemo(() => {
    if (coords) return coords;
    const saved = addresses?.find((address) => address.id === draft.addressId);
    return saved ? { lat: saved.lat, lng: saved.lng } : null;
  }, [coords, addresses, draft.addressId]);

  const [weekOffset, setWeekOffset] = React.useState(0);

  const range = React.useMemo(() => {
    const start = addDays(new Date(), 1 + weekOffset * 7);
    return {
      from: format(start, 'yyyy-MM-dd'),
      to: format(addDays(start, 6), 'yyyy-MM-dd'),
    };
  }, [weekOffset]);

  const params =
    resolved && draft.packageId
      ? { lat: resolved.lat, lng: resolved.lng, packageId: draft.packageId, ...range }
      : null;

  const { data, isLoading, error, refetch } = useAvailability(params);

  return (
    <StepShell
      title="Quand souhaitez-vous notre venue ?"
      description={
        data
          ? `Intervention de ${formatDuration(data.packageDurationMin)} — ${data.slotsRequired} creneau${data.slotsRequired > 1 ? 'x consecutifs' : ''} reserve${data.slotsRequired > 1 ? 's' : ''} chez le meme technicien.`
          : undefined
      }
      onBack={onBack}
      onNext={onNext}
      nextDisabled={!draft.slotIds?.length}
    >
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setWeekOffset((value) => Math.max(0, value - 1))}
          disabled={weekOffset === 0}
        >
          Semaine precedente
        </Button>
        <span className="text-sm text-muted-foreground">
          {formatRelativeDay(range.from)} — {formatRelativeDay(range.to)}
        </span>
        <Button variant="outline" size="sm" onClick={() => setWeekOffset((value) => value + 1)}>
          Semaine suivante
        </Button>
      </div>

      {isLoading ? <LoadingState rows={3} /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {data && data.totalOptions === 0 ? (
        <EmptyState
          title="Aucun creneau sur cette periode"
          description="Essayez la semaine suivante, ou choisissez un forfait plus court."
        />
      ) : null}

      <div className="flex flex-col gap-4">
        {data?.days.map((day) => (
          <section key={day.date} className="space-y-2">
            <h3 className="app-section-title capitalize">{formatRelativeDay(day.date)}</h3>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {day.options.map((option) => {
                const selected = draft.slotStartsAt === option.startsAt;

                return (
                  <button
                    key={option.startsAt + option.technicianId}
                    type="button"
                    onClick={() =>
                      patch({
                        slotIds: option.slotIds,
                        slotStartsAt: option.startsAt,
                        slotEndsAt: option.endsAt,
                        technicianName: option.technicianName,
                      })
                    }
                    className={cn(
                      'touch-target flex flex-col items-center justify-center gap-0.5 rounded-lg border px-2 py-2.5 transition-colors hover:bg-accent',
                      selected ? 'border-primary bg-accent' : 'border-border',
                    )}
                    aria-pressed={selected}
                  >
                    <span className="text-sm font-medium tabular-nums">
                      {formatTime(option.startsAt)}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      jusqu'a {formatTime(option.endsAt)}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </StepShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Etape 6 — Photos et produits
// ═══════════════════════════════════════════════════════════════════════════════
